/**
 * FinishProfilePage — required stop for new social/magic-link signups.
 *
 * The Archive requires every member to have a proper callsign identity
 * before entering /members. Social providers give a name, not a callsign,
 * so we force this picker one time. Existing members and password signups
 * already have a callsign minted at creation.
 *
 * If the member does NOT actually need this (needs_profile_completion=false),
 * this page auto-redirects to the intended /members target.
 */
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { socialApi, formatApiError } from "../../lib/authApi";
import { useMemberAuth } from "../../hooks/useMemberAuth";
import { AuthShell, Field, PrimaryBtn, Notice } from "../../components/auth/AuthShell";

const FinishProfilePage = () => {
  const nav = useNavigate();
  const location = useLocation();
  const { member, loading } = useMemberAuth();
  const [callsign, setCallsign] = useState("");
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const nextParam = new URLSearchParams(location.search).get("next") || "/members";

  useEffect(() => {
    if (loading) return;
    if (!member) {
      nav("/auth/login?next=" + encodeURIComponent(nextParam));
      return;
    }
    // Already has completed profile — skip
    if (!member.needs_profile_completion) {
      nav(nextParam, { replace: true });
    }
  }, [loading, member, nav, nextParam]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (!callsign || callsign.length < 3) {
      setErr("Choose a callsign at least 3 characters long.");
      return;
    }
    setBusy(true);
    try {
      await socialApi.completeProfile(callsign);
      // Trigger a member refresh so header/UI picks up new callsign
      // (setMemberSession calls _fetchMe internally when token is passed).
      const token = localStorage.getItem("archive_member_token");
      if (token) {
        // Simple reload of member cache via existing hook.
        // useMemberAuth's setMemberSession refetches when called with same token.
        const { setMemberSession } = await import("../../hooks/useMemberAuth");
        await setMemberSession(token);
      }
      nav(nextParam, { replace: true });
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Could not save callsign.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <AuthShell title="Loading" lead="Fetching your Archive identity…">
        <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#A39E93]">
          // WORKING…
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Pick your callsign"
      lead="Every operative on The Archive carries a callsign. This is how your comments, bookmarks, and dossier follows will be attributed. Choose carefully — you can change it later from your Member Desk."
    >
      {err && <Notice kind="error" data-testid="finish-profile-error">{err}</Notice>}
      <form onSubmit={onSubmit} data-testid="finish-profile-form">
        <Field
          label="CALLSIGN"
          type="text"
          value={callsign}
          onChange={setCallsign}
          required
          autoFocus
          data-testid="finish-profile-callsign"
          name="callsign"
        />
        <p className="font-mono text-[10px] leading-relaxed text-[#6F6A60] mb-4 -mt-2">
          3–60 characters. Letters, numbers, hyphens.
        </p>
        <PrimaryBtn disabled={busy || !callsign} data-testid="finish-profile-submit">
          {busy ? "SAVING…" : "CONTINUE"}
        </PrimaryBtn>
      </form>
    </AuthShell>
  );
};

export default FinishProfilePage;
